import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, Check, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/AppHeader";
import { JOB_STAGES } from "@/components/StageBadge";
import { DateField, TimeField } from "@/components/DateTimeField";
import { api, formatApiErrorDetail } from "@/lib/api";

const WINDOWS = ["All day", "AM", "PM"];

// Pull the job address from the account: main address first, then the legacy
// address field, then on-site / the first saved job address.
const accountAddress = (c) =>
  c?.main_address ||
  c?.address ||
  c?.onsite_address ||
  (c?.job_addresses || [])[0] ||
  "";

const EMPTY = {
  project_name: "",
  customer_id: "none",
  customer_name: "",
  address: "",
  sink_location: "Warehouse",
  sink_notes: "",
  stonify_url: "",
  raptor_url: "",
  stage: "Quote",
  template_date: "",
  template_window: "All day",
  template_time: "",
  template_crew: "none",
  fab_date: "",
  fab_window: "All day",
  fab_time: "",
  fab_crew: "none",
  install_date: "",
  install_window: "All day",
  install_time: "",
  install_crew: "none",
  summary: "",
  access_notes: "",
};

export default function JobForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [crew, setCrew] = useState([]);
  const [material, setMaterial] = useState({
    holds: [],
    slabs: [],
    remnants: [],
  });
  const [links, setLinks] = useState([]);
  const [matQuery, setMatQuery] = useState("");
  const [pendingPiece, setPendingPiece] = useState(null);
  const [custQuery, setCustQuery] = useState("");
  const [matTab, setMatTab] = useState("all");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [showManageAddr, setShowManageAddr] = useState(false);
  const [addrEdits, setAddrEdits] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/customers"),
      api.get("/crew-list"),
      api.get("/reservations"),
      api.get("/slabs"),
      api.get("/remnants"),
    ]).then(([c, u, holds, slabs, remnants]) => {
      setCustomers(c.data);
      setCrew(u.data);
      setMaterial({
        holds: holds.data,
        slabs: slabs.data,
        remnants: remnants.data,
      });
    });
  }, []);

  useEffect(() => {
    const preset = params.get("customer");
    if (id || !preset || customers.length === 0) return;
    const account = customers.find((x) => x.id === preset);
    if (!account) return;
    setForm((f) =>
      f.customer_id === preset
        ? f
        : {
            ...f,
            customer_id: account.id,
            customer_name: account.name,
            address: f.address || accountAddress(account),
          },
    );
  }, [id, params, customers]);

  // Opened from a project ("New job" on the project page): pull its name, account and address.
  useEffect(() => {
    const pid = params.get("project");
    if (id || !pid) return;
    api
      .get(`/projects/${pid}`)
      .then(({ data }) =>
        setForm((f) => ({
          ...f,
          project_id: pid,
          project_name: f.project_name || data.name || "",
          customer_id: data.customer_id || f.customer_id,
          customer_name: data.customer_name || f.customer_name,
          address: f.address || data.job_address || "",
        })),
      )
      .catch(() => {});
  }, [id, params]);

  useEffect(() => {
    if (!id) return;
    api.get(`/jobs/${id}`).then(({ data }) => {
      setForm({
        ...EMPTY,
        ...data,
        customer_id: data.customer_id || "none",
        template_crew: data.template_crew || "none",
        fab_crew: data.fab_crew || "none",
        install_crew: data.install_crew || "none",
        template_date: data.template_date || "",
        fab_date: data.fab_date || "",
        install_date: data.install_date || "",
      });
      setLinks(data.material_links || []);
    });
  }, [id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pickCustomer = (value) => {
    if (value === "none")
      return setForm((f) => ({ ...f, customer_id: "none", customer_name: "" }));
    const c = customers.find((x) => x.id === value);
    setForm((f) => ({
      ...f,
      customer_id: value,
      customer_name: c?.name || "",
      address: f.address || accountAddress(c),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      project_name: form.project_name,
      project_id: form.project_id || null,
      customer_id: form.customer_id === "none" ? null : form.customer_id,
      customer_name: form.customer_name,
      address: form.address,
      sink_location: form.sink_location,
      sink_notes: form.sink_notes,
      stonify_url: form.stonify_url,
      raptor_url: form.raptor_url,
      stage: form.stage,
      template_date: form.template_date || null,
      template_window: form.template_window,
      template_time: form.template_time,
      template_crew: form.template_crew === "none" ? "" : form.template_crew,
      fab_date: form.fab_date || null,
      fab_window: form.fab_window,
      fab_time: form.fab_time,
      fab_crew: form.fab_crew === "none" ? "" : form.fab_crew,
      install_date: form.install_date || null,
      install_window: form.install_window,
      install_time: form.install_time,
      install_crew: form.install_crew === "none" ? "" : form.install_crew,
      material_links: links,
      summary: form.summary,
      access_notes: form.access_notes,
    };
    try {
      const { data } = id
        ? await api.put(`/jobs/${id}`, payload)
        : await api.post("/jobs", payload);
      toast.success(id ? "Job updated" : `Job ${data.code} created`);
      navigate(`/projects/${data.id}?tab=field`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const scheduleBlock = (title, dateKey, windowKey, crewKey) => {
    const timeKey = `${dateKey.replace("_date", "")}_time`;
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          {title}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <DateField
            testId={`${dateKey.replace(/_/g, "-")}-input`}
            value={form[dateKey]}
            onChange={set(dateKey)}
          />
          <TimeField
            testId={`${timeKey.replace(/_/g, "-")}-input`}
            value={form[timeKey] || ""}
            onChange={set(timeKey)}
          />
          <Select value={form[windowKey]} onValueChange={set(windowKey)}>
            <SelectTrigger
              data-testid={`${windowKey.replace(/_/g, "-")}-select`}
              className="h-12 border-zinc-800 bg-zinc-950"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900">
              {WINDOWS.map((w) => (
                <SelectItem key={w} value={w}>
                  {w}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={form[crewKey]} onValueChange={set(crewKey)}>
            <SelectTrigger
              data-testid={`${crewKey.replace(/_/g, "-")}-select`}
              className="h-12 border-zinc-800 bg-zinc-950"
            >
              <SelectValue placeholder="Crew" />
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900">
              <SelectItem value="none">Unassigned</SelectItem>
              {crew.map((u) => (
                <SelectItem key={u.id} value={u.name || u.email}>
                  {u.name || u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  };

  const MATERIAL_TABS = [
    ["all", "All"],
    ["hold", "On hold"],
    ["slab", "Slabs"],
    ["piece", "Slabs in inventory"],
    ["remnant", "Remnants"],
  ];

  const allMaterial = [
    ...material.holds.map((i) => ({
      type: "hold",
      id: i.id,
      code: i.tag || "",
      label: i.slab_color || "",
      meta: `${i.customer_name || ""} ${i.project_name || ""}`.trim(),
    })),
    ...material.slabs.map((i) => ({
      type: "slab",
      id: i.id,
      code: i.code || "",
      label: i.slab_color || "",
      meta: `${i.material || ""} ${i.thickness || ""} · rack ${i.location || "—"}`,
    })),
    ...material.slabs.flatMap((i) =>
      (i.pieces || []).map((p) => ({
        type: "piece",
        id: `${i.id}:${p.id}`,
        code: p.serial_number || i.code || "",
        label: p.name || i.slab_color || "",
        reservedJob: p.reserved_job || "",
        meta: `${p.status || "In Stock"}${p.reserved_job ? ` · on ${p.reserved_job}` : ""}${
          p.bundle_number ? ` · bundle ${p.bundle_number}` : ""
        }${p.lot_number ? ` · lot ${p.lot_number}` : ""}`,
      })),
    ),
    ...material.remnants.map((i) => ({
      type: "remnant",
      id: i.id,
      code: i.code || "",
      label: i.slab_color || "",
      meta: `${Math.round(((i.usable_sqft || 0) - (i.allocated_sqft || 0)) * 10) / 10} ft² left · rack ${
        i.location || "—"
      }`,
    })),
  ];

  const filteredMaterial = allMaterial.filter((m) => {
    if (matTab !== "all" && m.type !== matTab) return false;
    const needle = matQuery.trim().toLowerCase();
    if (!needle) return true;
    return `${m.code} ${m.label} ${m.meta}`.toLowerCase().includes(needle);
  });

  const filteredCustomers = customers.filter((c) => {
    const needle = custQuery.trim().toLowerCase();
    if (!needle) return true;
    return `${c.name} ${c.company || ""} ${c.phone || ""} ${c.email || ""}`
      .toLowerCase()
      .includes(needle);
  });

  const selectedCustomer =
    form.customer_id && form.customer_id !== "none"
      ? customers.find((x) => x.id === form.customer_id) || null
      : null;

  // Saved addresses on the account, each tagged so staff know which site it is
  const addressOptions = selectedCustomer
    ? (() => {
        const raw = [
          ...(selectedCustomer.job_addresses || []).map((a) => ({
            value: a,
            kind: "Job",
          })),
          { value: selectedCustomer.main_address, kind: "Main" },
          { value: selectedCustomer.address, kind: "Main" },
          { value: selectedCustomer.onsite_address, kind: "On-site" },
          { value: selectedCustomer.billing_address, kind: "Billing" },
        ];
        const seen = new Set();
        const out = [];
        raw.forEach(({ value, kind }) => {
          const v = (value || "").trim();
          if (!v || seen.has(v)) return;
          seen.add(v);
          out.push({ value: v, kind });
        });
        return out;
      })()
    : [];
  const addressChoices = addressOptions.map((o) => o.value);

  const trimmedAddress = form.address.trim();
  const isNewAddress =
    !!selectedCustomer &&
    !!trimmedAddress &&
    !addressChoices.includes(trimmedAddress);

  const saveAddressToAccount = async () => {
    if (!selectedCustomer || !trimmedAddress) return;
    setSavingAddr(true);
    try {
      const next = [...(selectedCustomer.job_addresses || []), trimmedAddress];
      const { data } = await api.patch(`/customers/${selectedCustomer.id}`, {
        job_addresses: next,
      });
      setCustomers((list) => list.map((c) => (c.id === data.id ? data : c)));
      toast.success("Address saved to account");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save the address",
      );
    } finally {
      setSavingAddr(false);
    }
  };

  const patchCustomerAddresses = async (next) => {
    if (!selectedCustomer) return;
    setSavingAddr(true);
    try {
      const { data } = await api.patch(`/customers/${selectedCustomer.id}`, {
        job_addresses: next,
      });
      setCustomers((list) => list.map((c) => (c.id === data.id ? data : c)));
      setAddrEdits({});
      toast.success("Saved addresses updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not update addresses",
      );
    } finally {
      setSavingAddr(false);
    }
  };

  const renameJobAddress = (i) => {
    const list = selectedCustomer?.job_addresses || [];
    const val = (addrEdits[i] ?? list[i] ?? "").trim();
    if (!val) return;
    patchCustomerAddresses(list.map((x, j) => (j === i ? val : x)));
  };

  const removeJobAddress = (i) => {
    const list = selectedCustomer?.job_addresses || [];
    patchCustomerAddresses(list.filter((_, j) => j !== i));
  };

  const isLinked = (m) => links.some((l) => l.type === m.type && l.id === m.id);

  const toggleLink = (m) =>
    setLinks((list) =>
      isLinked(m)
        ? list.filter((l) => !(l.type === m.type && l.id === m.id))
        : [...list, { type: m.type, id: m.id, code: m.code, label: m.label }],
    );

  const pickMaterial = (m) => {
    if (!isLinked(m) && m.reservedJob && m.reservedJob !== form.code) {
      setPendingPiece(m);
      return;
    }
    toggleLink(m);
  };

  const materialPicker = (
    <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        Linked Material
      </div>

      {links.length > 0 && (
        <div
          className="flex flex-wrap gap-2"
          data-testid="linked-material-chips"
        >
          {links.map((l) => (
            <span
              key={`${l.type}:${l.id}`}
              data-testid={`linked-chip-${l.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-yellow-500/40 bg-yellow-500/10 px-3 py-1 text-xs text-yellow-300"
            >
              <span className="font-mono-data">{l.code}</span>
              <span className="text-zinc-300">{l.label}</span>
              <button
                type="button"
                data-testid={`unlink-${l.id}`}
                onClick={() =>
                  setLinks((list) =>
                    list.filter((x) => !(x.type === l.type && x.id === l.id)),
                  )
                }
                className="text-yellow-300/80 hover:text-white"
              >
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <MagnifyingGlass
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
        />
        <Input
          data-testid="material-search-input"
          value={matQuery}
          onChange={(e) => setMatQuery(e.target.value)}
          placeholder="Search color, code, rack, customer…"
          className="h-12 border-zinc-800 bg-zinc-950 pl-9"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {MATERIAL_TABS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            data-testid={`material-tab-${value}`}
            onClick={() => setMatTab(value)}
            className={`h-10 shrink-0 rounded-md border px-3 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
              matTab === value
                ? "border-indigo-500 bg-indigo-500 text-white"
                : "border-zinc-700 bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        className="max-h-64 space-y-2 overflow-y-auto pr-1"
        data-testid="material-results"
      >
        {filteredMaterial.length === 0 && (
          <p className="py-4 text-center text-sm text-zinc-500">
            No material matches that search.
          </p>
        )}
        {filteredMaterial.map((m) => (
          <button
            key={`${m.type}:${m.id}`}
            type="button"
            data-testid={`material-option-${m.id}`}
            onClick={() => pickMaterial(m)}
            className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors duration-150 ${
              isLinked(m)
                ? "border-yellow-500 bg-yellow-500/10"
                : "border-zinc-800 bg-zinc-950 hover:border-zinc-700 hover:bg-zinc-800"
            }`}
          >
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                isLinked(m)
                  ? "border-indigo-500 bg-indigo-500 text-white"
                  : "border-zinc-600"
              }`}
            >
              {isLinked(m) && <Check size={12} weight="bold" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="font-mono-data block text-xs text-yellow-500">
                {m.code}
              </span>
              <span className="block truncate text-sm font-semibold">
                {m.label}
              </span>
              <span className="block truncate text-xs text-zinc-500">
                {m.meta}
              </span>
            </span>
            {m.reservedJob && m.reservedJob !== form.code && (
              <span
                data-testid={`material-reserved-${m.id}`}
                className="shrink-0 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-red-400"
              >
                On {m.reservedJob}
              </span>
            )}
            <span className="shrink-0 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
              {m.type === "hold" ? "On hold" : m.type}
            </span>
          </button>
        ))}
      </div>

      <AlertDialog
        open={!!pendingPiece}
        onOpenChange={(open) => !open && setPendingPiece(null)}
      >
        <AlertDialogContent
          className="border-zinc-800 bg-zinc-900"
          data-testid="reserved-piece-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Already reserved</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {pendingPiece?.label} ({pendingPiece?.code || "no serial"}) is
              reserved on {pendingPiece?.reservedJob}. Linking it here moves the
              reservation to this job.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="reserved-piece-cancel"
              className="border-zinc-700"
            >
              Keep it there
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reserved-piece-confirm"
              onClick={() => {
                toggleLink(pendingPiece);
                setPendingPiece(null);
              }}
              className="bg-indigo-500 text-white hover:bg-indigo-400"
            >
              Link anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Edit job" : "New job"} />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-32 pt-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Job" : "New Job"}
        </h1>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5"
          data-testid="job-form"
        >
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Project Name
            </Label>
            <Input
              data-testid="job-project-input"
              required
              value={form.project_name}
              onChange={(e) => set("project_name")(e.target.value)}
              placeholder="Elm St kitchen countertops"
              className="h-12 border-zinc-800 bg-zinc-900 text-base"
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Customer
              </Label>
              <Select value={form.customer_id} onValueChange={pickCustomer}>
                <SelectTrigger
                  data-testid="job-customer-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  {form.customer_id && form.customer_id !== "none" ? (
                    <span data-testid="job-customer-linked">
                      {form.customer_name || "Linked customer"}
                    </span>
                  ) : (
                    <SelectValue placeholder="Link a customer" />
                  )}
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <div className="sticky top-0 z-10 bg-zinc-900 p-2">
                    <div className="relative">
                      <MagnifyingGlass
                        size={14}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
                      />
                      <Input
                        data-testid="job-customer-search"
                        value={custQuery}
                        onChange={(e) => setCustQuery(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder="Search customers…"
                        className="h-10 border-zinc-800 bg-zinc-950 pl-8 text-sm"
                      />
                    </div>
                  </div>
                  <SelectItem value="none">No customer</SelectItem>
                  {filteredCustomers.length === 0 && (
                    <p className="px-3 py-2 text-sm text-zinc-500">
                      No customers match.
                    </p>
                  )}
                  {filteredCustomers.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={c.id}
                      data-testid={`job-customer-${c.name}`}
                    >
                      {c.name}
                      {c.company ? ` — ${c.company}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Stage
              </Label>
              <Select value={form.stage} onValueChange={set("stage")}>
                <SelectTrigger
                  data-testid="job-stage-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {JOB_STAGES.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`job-stage-${s}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Job Address
              </Label>
              <div className="flex items-center gap-3">
                {isNewAddress && (
                  <button
                    type="button"
                    data-testid="job-address-save-to-account"
                    onClick={saveAddressToAccount}
                    disabled={savingAddr}
                    className="text-xs font-semibold text-emerald-400 transition-colors duration-150 hover:text-emerald-300 disabled:opacity-50"
                  >
                    {savingAddr ? "Saving…" : "Save to account"}
                  </button>
                )}
                {selectedCustomer?.billing_address?.trim() && (
                  <button
                    type="button"
                    data-testid="job-address-copy-billing"
                    onClick={() =>
                      set("address")(selectedCustomer.billing_address.trim())
                    }
                    className="text-xs font-semibold text-indigo-400 transition-colors duration-150 hover:text-indigo-300"
                  >
                    Same as billing
                  </button>
                )}
              </div>
            </div>
            {addressChoices.length > 1 && (
              <Select
                value={
                  addressChoices.includes(form.address) ? form.address : ""
                }
                onValueChange={(v) => set("address")(v)}
              >
                <SelectTrigger
                  data-testid="job-address-picker"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue placeholder="Pick a saved address…" />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {addressOptions.map((o, i) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`job-address-option-${i}`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span
                          data-testid={`job-address-option-kind-${i}`}
                          className="rounded-full border border-zinc-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400"
                        >
                          {o.kind}
                        </span>
                        {o.value}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Input
              data-testid="job-address-input"
              value={form.address}
              onChange={(e) => set("address")(e.target.value)}
              placeholder="142 Elm St, Springfield"
              className="h-12 border-zinc-800 bg-zinc-900 text-base"
            />
            {selectedCustomer &&
              (selectedCustomer.job_addresses || []).length > 0 && (
                <div className="pt-1">
                  <button
                    type="button"
                    data-testid="job-address-manage-toggle"
                    onClick={() => setShowManageAddr((v) => !v)}
                    className="text-xs font-semibold text-zinc-400 transition-colors duration-150 hover:text-white"
                  >
                    {showManageAddr
                      ? "Done managing"
                      : `Manage saved addresses (${selectedCustomer.job_addresses.length})`}
                  </button>
                  {showManageAddr && (
                    <div
                      className="mt-2 space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 p-2.5"
                      data-testid="job-address-manage"
                    >
                      {selectedCustomer.job_addresses.map((addr, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2"
                          data-testid={`job-address-manage-row-${i}`}
                        >
                          <Input
                            data-testid={`job-address-edit-${i}`}
                            value={addrEdits[i] ?? addr}
                            onChange={(e) =>
                              setAddrEdits((m) => ({
                                ...m,
                                [i]: e.target.value,
                              }))
                            }
                            className="h-10 flex-1 border-zinc-800 bg-zinc-900 text-sm"
                          />
                          <Button
                            type="button"
                            data-testid={`job-address-save-${i}`}
                            variant="outline"
                            disabled={
                              savingAddr ||
                              (addrEdits[i] ?? addr).trim() === addr.trim() ||
                              !(addrEdits[i] ?? addr).trim()
                            }
                            onClick={() => renameJobAddress(i)}
                            className="h-10 shrink-0 border-zinc-700 bg-transparent px-3 hover:bg-zinc-800 disabled:opacity-40"
                          >
                            <Check size={14} weight="bold" />
                          </Button>
                          <Button
                            type="button"
                            data-testid={`job-address-remove-${i}`}
                            variant="outline"
                            disabled={savingAddr}
                            onClick={() => removeJobAddress(i)}
                            className="h-10 shrink-0 border-zinc-700 bg-transparent px-3 text-red-400 hover:bg-zinc-800 disabled:opacity-40"
                          >
                            <X size={14} weight="bold" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Sink Location
              </Label>
              <Select
                value={form.sink_location}
                onValueChange={set("sink_location")}
              >
                <SelectTrigger
                  data-testid="job-sink-location-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {["Warehouse", "On-Site"].map((l) => (
                    <SelectItem
                      key={l}
                      value={l}
                      data-testid={`job-sink-location-${l}`}
                    >
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Sink Notes (model # / description)
              </Label>
              <Input
                data-testid="job-sink-notes-input"
                value={form.sink_notes}
                onChange={(e) => set("sink_notes")(e.target.value)}
                placeholder="Blanco 440150 · 30in single bowl"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Stonify Link
              </Label>
              <Input
                data-testid="job-stonify-input"
                type="url"
                value={form.stonify_url}
                onChange={(e) => set("stonify_url")(e.target.value)}
                placeholder="Paste Stonify link"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Raptor Link
              </Label>
              <Input
                data-testid="job-raptor-input"
                type="url"
                value={form.raptor_url}
                onChange={(e) => set("raptor_url")(e.target.value)}
                placeholder="Paste Raptor link"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              />
            </div>
          </div>
          {scheduleBlock(
            "Template",
            "template_date",
            "template_window",
            "template_crew",
          )}{" "}
          {scheduleBlock("Fabrication", "fab_date", "fab_window", "fab_crew")}
          {scheduleBlock(
            "Install",
            "install_date",
            "install_window",
            "install_crew",
          )}
          {materialPicker}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Job Summary
            </Label>
            <Textarea
              data-testid="job-summary-input"
              value={form.summary}
              onChange={(e) => set("summary")(e.target.value)}
              rows={4}
              placeholder="Scope, edge profile, cutouts, special instructions…"
              className="border-zinc-800 bg-zinc-900 text-base"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Gate / Entry Notes
            </Label>
            <Textarea
              data-testid="job-access-notes-input"
              value={form.access_notes || ""}
              onChange={(e) => set("access_notes")(e.target.value)}
              rows={2}
              placeholder="Gate code, lockbox, side door, dog in yard, call on arrival…"
              className="border-zinc-800 bg-zinc-900 text-base"
            />
          </div>
          {error && (
            <p
              data-testid="job-form-error"
              className="text-sm font-medium text-red-400"
            >
              {error}
            </p>
          )}
          <div className="no-print fixed bottom-0 left-0 right-0 border-t border-zinc-800 bg-zinc-950 p-3 sm:static sm:border-0 sm:bg-transparent sm:p-0">
            <Button
              data-testid="save-job-button"
              type="submit"
              disabled={busy}
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 sm:w-auto sm:px-10"
            >
              {busy ? "Saving…" : id ? "Save Changes" : "Create Job"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
